import React, { useEffect, useState } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from '../components/ui/dialog';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../components/ui/select';
import { BadgeCheck, Plus, Trash2, Edit3, Search, Shield, Mail, ExternalLink, ClipboardList, CheckCircle2, XCircle, FileText, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../services/api';

const SECTORS = ['insurance', 'wealth', 'solicitor', 'accountant', 'other'];

const emptyForm = () => ({
  firm_name: '',
  email_domain: '',
  fca_number: '',
  contact_email: '',
  contact_phone: '',
  sector: 'insurance',
  notes: '',
  active: true,
  testimonial_quote: '',
  testimonial_author: '',
  testimonial_role: '',
});

export default function AdminBrokers() {
  const [brokers, setBrokers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState('');
  const [applications, setApplications] = useState([]);
  const [appCounts, setAppCounts] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [appActionId, setAppActionId] = useState(null);

  const fetchData = async () => {
    // Use allSettled so a transient failure on either endpoint doesn't blank
    // the other panel (e.g. broken /api/brokers must not hide pending applications).
    const [r, ar] = await Promise.allSettled([
      api.listBrokers(),
      api.listBrokerApplications('pending'),
    ]);
    if (r.status === 'fulfilled') {
      setBrokers(r.value.data);
    } else {
      toast.error(r.reason?.response?.data?.detail || 'Could not load brokers');
    }
    if (ar.status === 'fulfilled') {
      setApplications(ar.value.data?.applications || []);
      setAppCounts(ar.value.data?.counts || { pending: 0, approved: 0, rejected: 0 });
    }
    setLoading(false);
  };

  const approveApp = async (app) => {
    if (!window.confirm(`Approve ${app.firm_name}? This creates a partner login for ${app.applicant_email} and emails them their credentials.`)) return;
    setAppActionId(app.id);
    try {
      await api.approveBrokerApplication(app.id);
      toast.success('Application approved. Partner login emailed.');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not approve');
    } finally {
      setAppActionId(null);
    }
  };

  const rejectApp = async (app) => {
    const reason = window.prompt('Reason for rejection (optional, included in the email):', '');
    if (reason === null) return; // cancelled
    setAppActionId(app.id);
    try {
      await api.rejectBrokerApplication(app.id, reason || null);
      toast.success('Application rejected.');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not reject');
    } finally {
      setAppActionId(null);
    }
  };

  const openProof = (appId) => {
    const baseUrl = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem('token');
    fetch(`${baseUrl}/api/brokers/applications/${appId}/proof`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(async (r) => {
      if (!r.ok) { toast.error('Proof unavailable'); return; }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      // Revoke after the new tab has had time to load the blob
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    }).catch(() => toast.error('Could not download proof'));
  };

  useEffect(() => { fetchData(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setOpen(true);
  };

  const openEdit = (b) => {
    setEditing(b);
    setForm({
      firm_name: b.firm_name || '',
      email_domain: b.email_domain || '',
      fca_number: b.fca_number || '',
      contact_email: b.contact_email || '',
      contact_phone: b.contact_phone || '',
      sector: b.sector || 'insurance',
      notes: b.notes || '',
      active: b.active !== false,
      testimonial_quote: b.testimonial_quote || '',
      testimonial_author: b.testimonial_author || '',
      testimonial_role: b.testimonial_role || '',
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      if (editing) {
        await api.updateBroker(editing.id, form);
        toast.success('Broker updated');
      } else {
        await api.createBroker(form);
        toast.success('Broker added');
      }
      setOpen(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (b) => {
    if (!window.confirm(`Remove ${b.firm_name} from the verified brokers list?`)) return;
    try {
      await api.deleteBroker(b.id);
      toast.success('Removed');
      fetchData();
    } catch {
      toast.error('Could not remove');
    }
  };

  const sendOnboarding = async (b) => {
    if (!b.contact_email) { toast.error('Add a contact email for this firm first'); return; }
    if (!window.confirm(`Send the co-branded onboarding email to ${b.contact_email}?`)) return;
    try {
      const r = await api.sendBrokerOnboarding(b.id);
      toast.success(r.data?.message || 'Onboarding email sent');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send');
    }
  };

  const filtered = brokers.filter(b => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      (b.firm_name || '').toLowerCase().includes(q) ||
      (b.email_domain || '').toLowerCase().includes(q) ||
      (b.fca_number || '').toLowerCase().includes(q)
    );
  });

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8 max-w-5xl mx-auto" data-testid="admin-brokers-page">
        <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-gold uppercase tracking-[0.2em] text-xs mb-2">Admin · Verified Brokers</p>
            <h1 className="font-serif text-3xl text-white">Trusted firms registry</h1>
            <p className="text-sm text-muted-foreground mt-2 max-w-2xl leading-relaxed">
              Add the firms whose email domains should display a "Verified" pill across the platform.
              Used by the Fix-this composer and the PII Share viewer to give clients an instant trust signal.
            </p>
          </div>
          <Button onClick={openCreate} className="btn-primary" data-testid="add-broker-btn">
            <Plus size={14} className="mr-1" /> Add broker
          </Button>
        </div>

        <div className="luxury-card p-3 mb-4 flex items-center gap-3">
          <Search size={14} className="text-muted-foreground" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search firm / domain / FCA #"
            className="flex-1 bg-transparent text-sm text-white placeholder:text-muted-foreground/60 focus:outline-none"
            data-testid="admin-brokers-search"
          />
        </div>

        {/* Pending applications */}
        {applications.length > 0 && (
          <div className="mb-8" data-testid="admin-broker-applications">
            <div className="flex items-center gap-2 mb-3">
              <ClipboardList size={14} className="text-gold" />
              <h2 className="text-sm uppercase tracking-wider text-white">Pending applications</h2>
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-gold/15 text-gold">{appCounts.pending}</span>
            </div>
            <div className="space-y-3">
              {applications.map(app => (
                <div key={app.id} className="luxury-card p-4" data-testid={`broker-application-${app.id}`}>
                  <div className="flex items-start gap-4 flex-wrap">
                    <div className="w-10 h-10 rounded bg-gold/10 border border-gold/30 flex items-center justify-center flex-shrink-0">
                      <ClipboardList size={16} className="text-gold" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-medium truncate">{app.firm_name}</p>
                      <p className="text-[11px] text-muted-foreground mt-0.5">
                        <span className="text-zinc-300">{app.applicant_name}</span>
                        <span className="mx-1 text-zinc-600">·</span>
                        <a href={`mailto:${app.applicant_email}`} className="text-gold hover:underline">{app.applicant_email}</a>
                        {app.fca_number && <><span className="mx-1 text-zinc-600">·</span>FCA #{app.fca_number}</>}
                        {app.sector && <><span className="mx-1 text-zinc-600">·</span><span className="capitalize">{app.sector}</span></>}
                      </p>
                      {app.notes && <p className="text-xs text-zinc-400 mt-2 italic line-clamp-3">"{app.notes}"</p>}
                      <p className="text-[10px] text-muted-foreground mt-2">Submitted {new Date(app.created_at).toLocaleString()}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {app.proof_path && (
                        <button
                          type="button"
                          onClick={() => openProof(app.id)}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 text-[11px] border border-[#262626] text-zinc-300 hover:border-gold/40 hover:text-gold rounded"
                          data-testid={`broker-app-proof-${app.id}`}
                          title="View FCA proof"
                        >
                          <FileText size={12} /> Proof
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => approveApp(app)}
                        disabled={appActionId === app.id}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-[11px] bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25 border border-emerald-500/30 rounded disabled:opacity-50"
                        data-testid={`broker-app-approve-${app.id}`}
                      >
                        {appActionId === app.id ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />} Approve
                      </button>
                      <button
                        type="button"
                        onClick={() => rejectApp(app)}
                        disabled={appActionId === app.id}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-[11px] bg-red-500/10 text-red-300 hover:bg-red-500/20 border border-red-500/25 rounded disabled:opacity-50"
                        data-testid={`broker-app-reject-${app.id}`}
                      >
                        <XCircle size={12} /> Reject
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="h-px bg-[#1A1A1A] my-6" />
          </div>
        )}

        {loading ? (
          <div className="space-y-3">{[0,1,2].map(i => <Skeleton key={i} className="h-16 bg-white/5" />)}</div>
        ) : filtered.length === 0 ? (
          <div className="luxury-card p-10 text-center" data-testid="admin-brokers-empty">
            <Shield size={28} className="text-muted-foreground mx-auto mb-3" />
            <h3 className="font-serif text-xl text-white mb-2">
              {brokers.length === 0 ? 'No verified brokers yet' : 'No brokers match'}
            </h3>
            <p className="text-sm text-muted-foreground">
              {brokers.length === 0
                ? 'Add a firm (e.g. Aon, Marsh, Hiscox) to issue trust badges across the app.'
                : 'Adjust your search.'}
            </p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="admin-brokers-list">
            {filtered.map(b => (
              <div key={b.id} className="luxury-card p-4 flex items-center gap-4" data-testid={`broker-row-${b.id}`}>
                <div className="w-10 h-10 rounded bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center flex-shrink-0">
                  <BadgeCheck size={18} className="text-emerald-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium truncate">
                    {b.firm_name}
                    {!b.active && <span className="ml-2 text-[10px] text-amber-400 uppercase tracking-wider">(inactive)</span>}
                  </p>
                  <p className="text-[11px] text-muted-foreground">
                    <span className="font-mono">@{b.email_domain}</span>
                    {b.fca_number && <><span className="mx-2 text-zinc-600">·</span>FCA #{b.fca_number}</>}
                    {b.sector && <><span className="mx-2 text-zinc-600">·</span><span className="capitalize">{b.sector}</span></>}
                  </p>
                </div>
                <button onClick={() => openEdit(b)} className="p-2 text-muted-foreground hover:text-gold" data-testid={`edit-${b.id}`}><Edit3 size={14} /></button>
                <a
                  href={`/brokers/${b.firm_slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 text-muted-foreground hover:text-emerald-400"
                  data-testid={`preview-${b.id}`}
                  title="Preview landing page"
                >
                  <ExternalLink size={14} />
                </a>
                <button
                  onClick={() => sendOnboarding(b)}
                  className="p-2 text-muted-foreground hover:text-[#627EEA]"
                  data-testid={`send-onboarding-${b.id}`}
                  title={b.onboarding_email_sent_at ? `Last sent ${new Date(b.onboarding_email_sent_at).toLocaleDateString()}` : 'Send onboarding email'}
                >
                  <Mail size={14} className={b.onboarding_email_sent_at ? 'text-[#627EEA]/60' : ''} />
                </button>
                <button onClick={() => remove(b)} className="p-2 text-muted-foreground hover:text-red-400" data-testid={`delete-${b.id}`}><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
        )}

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="bg-[#0F0F0F] border-[#262626] max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-serif text-white">{editing ? 'Edit broker' : 'Add verified broker'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div>
                <Label className="text-muted-foreground text-xs">Firm name *</Label>
                <Input value={form.firm_name} onChange={e => { const v = e.target.value; setForm(p => ({...p, firm_name: v})); }} className="luxury-input" required data-testid="broker-firm-name" />
              </div>
              <div>
                <Label className="text-muted-foreground text-xs">Email domain * <span className="text-[10px] opacity-70">(e.g. aon.com)</span></Label>
                <Input value={form.email_domain} onChange={e => { const v = e.target.value; setForm(p => ({...p, email_domain: v})); }} className="luxury-input font-mono" placeholder="aon.com" required data-testid="broker-domain" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-muted-foreground text-xs">FCA number</Label>
                  <Input value={form.fca_number} onChange={e => { const v = e.target.value; setForm(p => ({...p, fca_number: v})); }} className="luxury-input" placeholder="123456" data-testid="broker-fca" />
                </div>
                <div>
                  <Label className="text-muted-foreground text-xs">Sector</Label>
                  <Select value={form.sector} onValueChange={v => setForm(p => ({...p, sector: v}))}>
                    <SelectTrigger className="luxury-input"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0F0F0F] border-[#262626]">
                      {SECTORS.map(s => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-muted-foreground text-xs">Contact email</Label>
                  <Input type="email" value={form.contact_email} onChange={e => { const v = e.target.value; setForm(p => ({...p, contact_email: v})); }} className="luxury-input" data-testid="broker-contact-email" />
                </div>
                <div>
                  <Label className="text-muted-foreground text-xs">Phone</Label>
                  <Input value={form.contact_phone} onChange={e => { const v = e.target.value; setForm(p => ({...p, contact_phone: v})); }} className="luxury-input" data-testid="broker-phone" />
                </div>
              </div>
              <div>
                <Label className="text-muted-foreground text-xs">Notes</Label>
                <Input value={form.notes} onChange={e => { const v = e.target.value; setForm(p => ({...p, notes: v})); }} className="luxury-input" />
              </div>

              <div className="pt-3 mt-2 border-t border-[#262626] space-y-3">
                <p className="text-[10px] uppercase tracking-wider text-gold/80">Testimonial (optional) - shown on the public landing page</p>
                <div>
                  <Label className="text-muted-foreground text-xs">Quote</Label>
                  <textarea
                    value={form.testimonial_quote}
                    onChange={e => { const v = e.target.value; setForm(p => ({...p, testimonial_quote: v})); }}
                    rows={3}
                    maxLength={400}
                    placeholder="e.g. Assagart has become indispensable for our client onboarding..."
                    className="w-full bg-[#0A0A0A] border border-[#262626] rounded p-2 text-sm text-white focus:outline-none focus:border-gold/40"
                    data-testid="broker-testimonial-quote"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-muted-foreground text-xs">Author</Label>
                    <Input value={form.testimonial_author} onChange={e => { const v = e.target.value; setForm(p => ({...p, testimonial_author: v})); }} className="luxury-input" placeholder="Victoria Sterling" data-testid="broker-testimonial-author" />
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-xs">Role</Label>
                    <Input value={form.testimonial_role} onChange={e => { const v = e.target.value; setForm(p => ({...p, testimonial_role: v})); }} className="luxury-input" placeholder="Managing Partner, Sterling Wealth" data-testid="broker-testimonial-role" />
                  </div>
                </div>
              </div>

              <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                <input type="checkbox" checked={form.active} onChange={e => { const v = e.target.checked; setForm(p => ({...p, active: v})); }} className="accent-gold" />
                Active (badge is shown across the app)
              </label>
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
                <Button type="submit" disabled={busy} className="btn-primary" data-testid="broker-save-btn">
                  {busy ? 'Saving...' : (editing ? 'Save' : 'Add broker')}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
